import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import apiService from "../../../../../service/Api";

interface CrewMember {
  crew_member_id: number;
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  streams_attended: number;
  profile_picture?: string | null;
}

interface CrewLowAttendanceResponse {
  threshold: number;
  count: number;
  results: CrewMember[];
}

const getGradientByName = (name: string) => {
  const hash = name.split("").reduce((acc, char) => char.charCodeAt(0) + ((acc << 5) - acc), 0);
  const colors = [
    ["#ff9a9e", "#fecfef"],
    ["#a1c4fd", "#c2e9fb"],
    ["#76b852", "#8DC26F"],
    ["#f12711", "#f5af19"],
    ["#a80077", "#667db6"],
    ["#3a7bd5", "#3a6073"],
    ["#f857a6", "#ff5858"],
    ["#ffd1ff", "#f107a3"],
    ["#11998e", "#38ef7d"],
  ];
  const idx = Math.abs(hash) % colors.length;
  return `linear-gradient(135deg, ${colors[idx][0]} 0%, ${colors[idx][1]} 100%)`;
};

const StreamRecordKeeper = () => {
  const [members, setMembers] = useState<CrewMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLowAttendance = async () => {
      try {
        setLoading(true);
        const now = new Date();
        const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
        const res = await apiService.get<CrewLowAttendanceResponse>(
          `api/management/dashboard/crew-low-attendance/?month=${month}`,
        );
        setMembers(res.data?.results ?? []);
      } catch (err: unknown) {
        const errorMessage =
          (err as { message?: string })?.message ||
          "Failed to fetch crew attendance.";
        setError(errorMessage);
        setMembers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLowAttendance();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 250,
          bgcolor: "#0f172a",
          borderRadius: 3,
          mt: 2,
        }}
      >
        <CircularProgress size={36} sx={{ color: "#f43f5e" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2, bgcolor: "#1e293b", color: "#f87171" }}>
        {error}
      </Alert>
    );
  }

  return <CrewAttendanceCard members={members} />;
};

const CrewAttendanceCard = ({ members }: { members: CrewMember[] }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        overflow: "hidden",
        background: "linear-gradient(180deg, #0c152b 0%, #070d19 100%)",
        border: "1px solid rgba(255, 255, 255, 0.08)",
        boxShadow: "0 12px 40px rgba(0, 0, 0, 0.4)",
        mt: 2,
        p: 2.5,
        color: "#ffffff",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 800,
            letterSpacing: "0.05em",
            color: "#ffffff",
            textTransform: "uppercase",
            fontSize: "14px",
          }}
        >
          Watchlist
        </Typography>
        <Box
          sx={{
            bgcolor: "rgba(244, 63, 94, 0.15)",
            color: "#f43f5e",
            px: 1.5,
            py: 0.5,
            borderRadius: 3,
            fontSize: "11px",
            fontWeight: 700,
            textTransform: "uppercase",
          }}
        >
          {"<"} 10 Streams
        </Box>
      </Box>

      {/* Watchlist List */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, flexGrow: 1 }}>
        {members.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: "rgba(255, 255, 255, 0.4)", textAlign: "center", py: 4 }}
          >
            No crew members with low attendance.
          </Typography>
        ) : (
          members.map((member) => (
            <MemberRow key={member.crew_member_id} member={member} />
          ))
        )}
      </Box>
    </Paper>
  );
};

const MemberRow = ({ member }: { member: CrewMember }) => {
  const fullName = `${member.first_name} ${member.last_name}`;
  const initials = `${member.first_name.charAt(0)}${member.last_name.charAt(0)}`.toUpperCase();

  // Progress threshold is 10 streams
  const progressPercent = Math.min((member.streams_attended / 10) * 100, 100);

  // Set colors based on attendance levels
  const getProgressColor = (attended: number) => {
    if (attended < 4) return "#f43f5e"; // soft red
    if (attended < 7) return "#fbbf24"; // amber/yellow
    return "#38ef7d"; // soft green
  };

  const progressColor = getProgressColor(member.streams_attended);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        bgcolor: "rgba(255, 255, 255, 0.04)",
        border: "1px solid rgba(255, 255, 255, 0.03)",
        p: 2,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          transform: "translateY(-2px)",
          bgcolor: "rgba(255, 255, 255, 0.07)",
          borderColor: "rgba(255, 255, 255, 0.08)",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Avatar
          src={member.profile_picture || undefined}
          sx={{
            width: 36,
            height: 36,
            background: getGradientByName(fullName),
            fontWeight: 700,
            fontSize: "13px",
            color: "#ffffff",
          }}
        >
          {initials}
        </Avatar>

        <Box sx={{ ml: 2, flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            noWrap
            sx={{
              fontWeight: 700,
              color: "#ffffff",
              textTransform: "capitalize",
              textOverflow: "ellipsis",
              overflow: "hidden",
            }}
          >
            {member.first_name} {member.last_name.toLowerCase()}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "rgba(255, 255, 255, 0.4)", fontSize: "11px" }}
          >
            Target: 10 streams
          </Typography>
        </Box>

        <Typography
          variant="body2"
          sx={{
            fontWeight: 800,
            color: progressColor,
            textAlign: "right",
            fontSize: "13px",
          }}
        >
          {member.streams_attended} / 10
        </Typography>
      </Box>

      {/* Custom Progress Bar */}
      <Box
        sx={{
          width: "100%",
          bgcolor: "rgba(255, 255, 255, 0.06)",
          borderRadius: 2,
          height: 6,
          overflow: "hidden",
          mt: 0.5,
        }}
      >
        <Box
          sx={{
            width: `${progressPercent}%`,
            bgcolor: progressColor,
            height: "100%",
            borderRadius: 2,
            transition: "width 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow: `0 0 8px ${progressColor}80`,
          }}
        />
      </Box>
    </Box>
  );
};

export default StreamRecordKeeper;
